import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAttachCareerLessonVideoMutation } from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { careerErrorMessage, ICareerLessonMedia } from './careerModel';

interface ICareerLessonVideoUploadHook {
  /**
   * Upload one file as the lesson's video in a single multipart request. The
   * `File` is handed straight to `uploadCareerLessonVideo` as an `Upload`
   * scalar; the backend writes the object server-side. Resolves to the lesson's
   * updated media on success, or `null` on any failure (a toast is raised for
   * the failure). The lesson must already be saved (have an `id`) — the caller
   * gates the control on that.
   */
  uploadVideo: (
    lessonId: string,
    file: File,
  ) => Promise<ICareerLessonMedia | null>;
  /** True from the moment the upload starts until it settles. */
  uploading: boolean;
}

/**
 * Spec 0020 — the career-lesson video upload. The picked `File` travels as an
 * `Upload` scalar over the GraphQL multipart request spec (apollo-upload-client
 * turns it into the multipart body; the terminating Apollo link is
 * `createUploadLink` — see `core/graphql/client/client.ts`), exactly like
 * `uploadStudentDiploma`. The backend writes the bytes itself — there is no
 * presigned S3 PUT and no bucket CORS. Multipart gives no granular upload
 * progress, so the caller shows an indeterminate busy state off `uploading`.
 */
export const useCareerLessonVideoUpload = (
  onChanged?: () => void,
): ICareerLessonVideoUploadHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [uploadRun] = useAttachCareerLessonVideoMutation();
  const [uploading, setUploading] = useState(false);

  const toastError = useCallback(
    (text?: string) => {
      addToast({
        type: GlobalToastTypeNames.CareerCenter,
        data: {
          type: ToastTypes.error,
          title: t('pages.careerCenter.lessons.toastVideoError'),
          text,
        },
      });
    },
    [addToast, t],
  );

  const uploadVideo: ICareerLessonVideoUploadHook['uploadVideo'] = useCallback(
    async (lessonId, file) => {
      setUploading(true);
      try {
        const result = await uploadRun({
          variables: { lessonId, video: file },
        });
        const payload = result.data?.uploadCareerLessonVideo;
        if (payload?.success && payload.lesson) {
          onChanged?.();
          return {
            videoUrl: payload.lesson.videoUrl ?? null,
            attachments: payload.lesson.attachments,
          };
        }
        toastError(careerErrorMessage(t, payload?.customErrors));
        return null;
      } catch (e) {
        toastError((e as Error).message);
        return null;
      } finally {
        setUploading(false);
      }
    },
    [uploadRun, onChanged, toastError, t],
  );

  return { uploadVideo, uploading };
};
